import * as Tone from "tone";
import { ensureAudioReady, kickAudio, onAudioStop, stopAllAudio } from "./piano";

/**
 * The shared click.
 *
 * Tone.Transport is global, so anything that calls `stopAllAudio()` — every
 * play button in the app — also stops the metronome. That used to happen
 * silently, leaving the metronome button lit while nothing was sounding.
 * The metronome now joins the same stop bus: it takes the transport
 * deliberately when it starts, and reports back when something else takes it away.
 */

let click: Tone.MembraneSynth | null = null;
let loopId: number | null = null;
let beatCount = 0;
let onTickCb: ((beat: number) => void) | null = null;
let onStoppedCb: (() => void) | null = null;
let unsubscribe: (() => void) | null = null;
let running = false;

function ensureClick() {
  if (!click) {
    click = new Tone.MembraneSynth({
      pitchDecay: 0.01,
      octaves: 4,
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.05 },
    }).toDestination();
    click.volume.value = -10;
  }
  return click;
}

export interface MetronomeOptions {
  bpm?: number;
  beatsPerBar?: number;
  onTick?: (beat: number) => void;
  /**
   * Called when the metronome stops for ANY reason — including another
   * component taking over the transport. Use it to reset your button so it
   * can never sit lit over silence.
   */
  onStopped?: () => void;
}

export function isMetronomeRunning() {
  return running;
}

/** Detach from the stop bus and clear our repeat. Does not stop the transport. */
function teardown() {
  if (loopId !== null) {
    Tone.getTransport().clear(loopId);
    loopId = null;
  }
  unsubscribe?.();
  unsubscribe = null;
  beatCount = 0;
  running = false;
}

export async function startMetronome(opts: MetronomeOptions = {}) {
  const { bpm = 90, beatsPerBar = 4, onTick, onStopped } = opts;
  kickAudio();
  // Take the transport deliberately — and let every other player reset its UI
  // — rather than colliding with whatever was already scheduled on it.
  stopAllAudio();
  teardown();
  await ensureAudioReady();
  const c = ensureClick();

  const t = Tone.getTransport();
  t.bpm.value = bpm;
  t.loop = false;
  beatCount = 0;
  onTickCb = onTick ?? null;
  onStoppedCb = onStopped ?? null;
  running = true;

  loopId = t.scheduleRepeat((time) => {
    const isDownbeat = beatCount % beatsPerBar === 0;
    c.triggerAttackRelease(isDownbeat ? "C5" : "G4", "32n", time);
    if (onTickCb) {
      const localBeat = beatCount % beatsPerBar;
      Tone.Draw.schedule(() => onTickCb && onTickCb(localBeat), time);
    }
    beatCount++;
  }, "4n");

  // Someone else started a sound: our click is already gone. Say so.
  unsubscribe = onAudioStop(() => {
    if (!running) return;
    const cb = onStoppedCb;
    teardown();
    onStoppedCb = null;
    onTickCb = null;
    cb?.();
  });

  t.position = "0:0:0";
  if (t.state !== "started") t.start("+0.05");
}

export function stopMetronome() {
  if (!running) return;
  const t = Tone.getTransport();
  teardown();
  onStoppedCb = null;
  onTickCb = null;
  t.stop();
  t.position = "0:0:0";
}

export function setMetronomeBpm(bpm: number) {
  Tone.getTransport().bpm.rampTo(bpm, 0.1);
}
